/*
 * Copyright (c) 2023 Helvia Technologies PC <contact@helvia.io>
 */
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";

// components imports
import { Button, Form, Input } from "antd";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuthUser } from "@/api/user";
import { ErrorsComponent } from "@/components/UI/ErrorsComponent";

// misc imports
import {
  NAV_FORGOT_PASSWORD,
  NAV_PROJECTS,
  NAV_REGISTRATION,
} from "@/constants";
import { IRequestError } from "@/interfaces/ServerError.interface";
import replaceUrlParams from "@/utils/string/replaceUrlParams";
import { useWorkspacesList } from "@/api/workspaces";
import { queryClient } from "@/utils/queryClient";
import { ITokens } from "@/interfaces/User.interface";

type FieldType = {
  first_name?: string;
  last_name?: string;
  company?: string;
  email?: string;
  password?: string;
};

const validateMessages = {
  required: "${label} is required!",
  types: {
    email: "${label} is not a valid email!",
  },
};

/**
 * Login Form component
 * @returns {JSX.Element}
 */
export const LoginForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  // const [accessToken, setAccessToken] = useState<string | undefined>(undefined);
  const userAuth:ITokens | undefined = queryClient.getQueryData(["user-auth"]);

  const { access: accessToken } = userAuth || {};
  const location = useLocation();
  
  const { data: workspacesList, error: workspaceError } = useWorkspacesList({
    enabled: !!accessToken,
  });

  const { mutate: authenticationMutation, isLoading, error: userAuthError } = useAuthUser();

  const handleSuccessLogin = useCallback(() => {
    const urlSearchString = new URLSearchParams(location.search);
    const redirectUrl = urlSearchString.get("next");

    if (!workspacesList) return; // FIXME: should not be like that. If there is no workspace we should create one
    if (redirectUrl) {
      navigate(redirectUrl);
    } else {
      navigate(
        replaceUrlParams(NAV_PROJECTS, {
          workspaceId: workspacesList[0]?.uuid,
        })
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, workspacesList]);

  useEffect(() => {
    if (accessToken && workspacesList && workspacesList.length > 0) {
      handleSuccessLogin();
    }
  }, [accessToken, handleSuccessLogin, workspacesList]);

  const onFinish = (values: any) => {
    authenticationMutation(values);
  };

  return (
    <Form
      name="login-form"
      form={form}
      layout="vertical"
      // labelCol={{ span: AUTH_FORM_LABEL_COL }}
      // labelAlign="left"
      // wrapperCol={{ span: AUTH_FORM_WRAPPER_COL }}
      // style={{ maxWidth: AUTH_FORM_MAX_WIDTH }}
      onFinish={onFinish}
      autoComplete="off"
      initialValues={{
        email: location.state?.email || ''
      }}
      validateMessages={validateMessages}
    >
      {/* Email */}
      <Form.Item<FieldType>
        label={t("common.email")}
        name="email"
        rules={[
          {
            required: true,
            type: "email",
          },
        ]}
      >
        <Input />
      </Form.Item>
      {/* ./ Email */}
      {/* Password */}
      <Form.Item<FieldType>
        label={t("common.password")}
        name="password"
        rules={[{ required: true }]}
      >
        <Input.Password />
      </Form.Item>
      {/* ./ Password */}

      {/* Forgot password */}
      <Form.Item shouldUpdate noStyle>
        {() => (
          <Form.Item>
            <Link
              to={NAV_FORGOT_PASSWORD}
              state={{ email: form.getFieldValue("email") }}
              className="float-right clearfix"
            >
              {t("login.forgot_password")}
            </Link>
          </Form.Item>
        )}
      </Form.Item>
      {/* ./ Forgot password */}

      {/* Submit button */}
      <Form.Item>
        <Button className="w-100" disabled={isLoading} type="primary" htmlType="submit">
          {t("common.sign_in")}
        </Button>
        <div className="mt-3">
          <ErrorsComponent error={userAuthError as unknown as IRequestError} />
          <ErrorsComponent error={workspaceError as unknown as IRequestError} />
        </div>
        <p className="mt-3">
          {t("login.no_account")}{" "}
          <Link to={NAV_REGISTRATION}>{t("common.create_user")}</Link>
        </p>
      </Form.Item>
      {/* Submit button */}
    </Form>
  );
};
